import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { obtenerEvaluaciones, obtenerReporteProgreso, obtenerReporteResumen, obtenerAmbitoAprendizaje, obtenerTodosLosNinos } from '../../services/evaluacionesService';
import { obtenerCentro } from '../../services/centroService';
import { formatearFechaLocal } from '../../utils/dateUtils';
import EvaluationCharts from '../../components/EvaluationCharts';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, Svg, Line, Rect } from '@react-pdf/renderer';
import { FaCalendarAlt, FaUser, FaSchool, FaClipboardList, FaDownload, FaEye, FaSearch, FaFilter } from 'react-icons/fa';
import { FileDown, PieChart, User, ChevronRight, CalendarDays } from 'lucide-react';
import LogoJireh from '../../assets/logo.jpg';

// Colores para las gráficas
const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6'];

// Función para determinar el nivel de logro
const determinarLogroEquivalente = (porcentaje) => {
   if (porcentaje <= 66.00) return "NO LO CONSIGUE";
   if (porcentaje <= 86.00) return "EN PROCESO";
   if (porcentaje < 101.00) return "DOMINA EL LOGRO";
   return "DOMINA EL LOGRO";
};

// Función para calcular la edad
const calcularEdad = (fechaNacimiento) => {
   if (!fechaNacimiento) return 'No especificada';
   
   const fechaNac = new Date(fechaNacimiento);
   const hoy = new Date();
   
   let años = hoy.getFullYear() - fechaNac.getFullYear();
   let meses = hoy.getMonth() - fechaNac.getMonth();
   
   if (meses < 0 || (meses === 0 && hoy.getDate() < fechaNac.getDate())) {
       años--;
       meses = 12 + meses;
   }
   
   if (hoy.getDate() < fechaNac.getDate()) {
       meses--;
   }
   
   if (años < 1) {
       return `${meses} meses`;
   } else if (años === 1) {
       return meses === 0 ? 
           '1 año' : 
           `1 año y ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
   } else {
       return meses === 0 ? 
           `${años} años` : 
           `${años} años y ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
   }
};

// Estilos para el PDF
const styles = StyleSheet.create({
    page: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        fontSize: 9
    },
    header: {
        marginBottom: 15,
        textAlign: 'center',
        borderWidth: 1,
        borderColor: '#000000',
        padding: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center'
    },
    headerLogo: {
        width: 50,
        height: 50,
        marginRight: 8
    },
    headerContent: {
        flex: 1,
        textAlign: 'center'
    },
    headerTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 4
    },
    headerSubtitle: {
        fontSize: 10,
        textAlign: 'center',
        marginBottom: 4
    },
    section: {
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#000000',
        padding: 6
    },
    sectionTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        backgroundColor: '#E5E7EB',
        padding: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    infoGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6
    },
    infoRow: {
        flexDirection: 'row',
        marginBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        paddingVertical: 2
    },
    infoLabel: {
        width: 110,
        fontWeight: 'bold',
        fontSize: 8
    },
    infoValue: {
        flex: 1,
        fontSize: 8
    },
    ambitosTable: {
        width: '100%',
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#000000'
    },
    ambitosHeader: {
        flexDirection: 'row',
        backgroundColor: '#E5E7EB',
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    ambitosHeaderCell: {
        padding: 3,
        flex: 1,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    ambitosRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        minHeight: 20
    },
    ambitosCell: {
        padding: 3,
        flex: 1,
        textAlign: 'left',
        fontSize: 7,
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    chartSection: {
        marginTop: 10,
        padding: 10,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#000000'
    },
    chartTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
        backgroundColor: '#E5E7EB',
        padding: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    barChartContainer: {
        width: '100%',
        maxHeight: 200,
        marginBottom: 10
    },
    barLabel: {
        fontSize: 8,
        textAlign: 'right',
        marginRight: 5,
        width: 120
    },
    percentageLabel: {
        fontSize: 8,
        marginLeft: 5,
        width: 40
    },
    resumenTable: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#000000',
        marginTop: 10
    },
    resumenRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    resumenCell: {
        padding: 4,
        flex: 1,
        fontSize: 8,
        textAlign: 'center',
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    resumenHeader: {
        flexDirection: 'row',
        backgroundColor: '#E5E7EB',
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    },
    resumenHeaderCell: {
        padding: 4,
        flex: 1,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#000000'
    },
    resumenTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 8,
        backgroundColor: '#E5E7EB',
        padding: 4,
        borderBottomWidth: 1,
        borderBottomColor: '#000000'
    }
});

// Componente de Gráfica de Barras para PDF
const PDFBarChart = ({ data }) => {
   const maxWidth = 300;
   const barHeight = 20;
   const spacing = 8;
   const maxPercentage = 100;

   return (
       <View style={styles.barChartContainer}>
           {data.map((item, index) => (
               <View key={index} style={{ flexDirection: 'row', marginBottom: spacing, alignItems: 'center' }}>
                   <Text style={styles.barLabel} numberOfLines={2}>{item.name}</Text>
                   <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                       <Svg width={maxWidth} height={barHeight}>
                           <Rect
                               x={0}
                               y={0}
                               width={(item.porcentaje / maxPercentage) * maxWidth}
                               height={barHeight}
                               fill={COLORS[index % COLORS.length]}
                               rx={2}
                               ry={2}
                           />
                       </Svg>
                       <Text style={styles.percentageLabel}>{`${item.porcentaje.toFixed(1)}%`}</Text>
                   </View>
               </View>
           ))}
       </View>
   );
};

// Componente PDF
const ReportePDF = ({ centro, nino, evaluacion, reportes }) => {
   const datosGrafica = React.useMemo(() => {
       return reportes?.datosReporteProgreso?.data?.map((ambito, index) => ({
           name: ambito.ambito_aprendizaje,
           porcentaje: parseFloat(ambito.porcentaje_ambito),
           fill: COLORS[index % COLORS.length]
       })) || [];
   }, [reportes]);

   return (
       <Document>
           <Page size="A4" style={styles.page} wrap>
               {/* Encabezado */}
               <View style={styles.header} fixed>
                   <Image 
                       src={LogoJireh}
                       style={styles.headerLogo}
                   />
                   <View style={styles.headerContent}>
                       <Text style={styles.headerTitle}>
                           {`CENTRO DE DESARROLLO INFANTIL "${centro?.nombre_unidad?.toUpperCase() || 'SIN NOMBRE'}"`}
                       </Text>
                       <Text style={styles.headerSubtitle}>FICHA DE INDICADORES DEL DESARROLLO{"\n"}INFANTIL INTEGRAL</Text>
                   </View>
               </View>

               {/* Sección 1: Datos del Centro */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>1. DATOS DEL CENTRO:</Text>
                   <View style={styles.infoGrid}>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>ZONA:</Text>
                           <Text style={styles.infoValue}>{centro?.zona}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>CANTÓN:</Text>
                           <Text style={styles.infoValue}>{centro?.canton}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>PROVINCIA:</Text>
                           <Text style={styles.infoValue}>{centro?.provincia}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>PARROQUIA:</Text>
                           <Text style={styles.infoValue}>{centro?.parroquia}</Text>
                       </View>
                   </View>
               </View>

               {/* Sección 2: Datos del Niño */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>2. DATOS DEL NIÑO/A:</Text>
                   <View style={styles.infoGrid}>
                       <View style={[styles.infoRow, { width: '100%' }]}>
                           <Text style={styles.infoLabel}>NOMBRES Y APELLIDOS:</Text>
                           <Text style={styles.infoValue}>{nino?.nombre} {nino?.apellidos}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>CÉDULA:</Text>
                           <Text style={styles.infoValue}>{nino?.cedula || 'No registrada'}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>FECHA DE NACIMIENTO:</Text>
                           <Text style={styles.infoValue}>{new Date(nino?.fecha_nacimiento).toLocaleDateString()}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>EDAD:</Text>
                           <Text style={styles.infoValue}>{calcularEdad(nino?.fecha_nacimiento)}</Text>
                       </View>
                       <View style={[styles.infoRow, { width: '48%' }]}>
                           <Text style={styles.infoLabel}>FECHA DE EVALUACIÓN:</Text>
                           <Text style={styles.infoValue}>{new Date(evaluacion?.fecha).toLocaleDateString()}</Text>
                       </View>
                   </View>
               </View>

               {/* Sección 3: Resultados */}
               <View style={styles.section} wrap={false}>
                   <Text style={styles.sectionTitle}>3. RESULTADOS DE LA EVALUACIÓN</Text>
                   <View style={styles.chartSection}>
                       <Text style={styles.chartTitle}>PROGRESO POR ÁMBITOS</Text>
                       <PDFBarChart data={datosGrafica} />
                   </View>

                   {/* Tabla de Resumen Detallado */}
                   <View style={styles.section} wrap={false}>
                       <Text style={styles.resumenTitle}>RESUMEN DETALLADO DE LA EVALUACIÓN</Text>
                       <View style={styles.resumenTable}>
                           <View style={styles.resumenHeader}>
                               <Text style={[styles.resumenHeaderCell, { flex: 2 }]}>ÁMBITO</Text>
                               <Text style={[styles.resumenHeaderCell, { flex: 1 }]}>PORCENTAJE</Text>
                               <Text style={[styles.resumenHeaderCell, { flex: 1.5, borderRightWidth: 0 }]}>NIVEL DE LOGRO</Text>
                           </View>
                           {datosGrafica.map((item, index) => (
                               <View key={index} style={styles.resumenRow}>
                                   <Text style={[styles.resumenCell, { flex: 2 }]}>{item.name}</Text>
                                   <Text style={[styles.resumenCell, { flex: 1 }]}>{item.porcentaje.toFixed(1)}%</Text>
                                   <Text style={[styles.resumenCell, { flex: 1.5, borderRightWidth: 0 }]}>
                                       {determinarLogroEquivalente(item.porcentaje)}
                                   </Text>
                               </View>
                           ))}
                           {reportes?.datosReporteResumen?.data && (
                               <View style={[styles.resumenRow, { backgroundColor: '#F3F4F6' }]}>
                                   <Text style={[styles.resumenCell, { flex: 2, fontWeight: 'bold' }]}>RESUMEN GENERAL</Text>
                                   <Text style={[styles.resumenCell, { flex: 1, fontWeight: 'bold' }]}>
                                       {parseFloat(reportes.datosReporteResumen.data.porcentaje_total || 0).toFixed(1)}%
                                   </Text>
                                   <Text style={[styles.resumenCell, { flex: 1.5, borderRightWidth: 0, fontWeight: 'bold' }]}>
                                       {determinarLogroEquivalente(parseFloat(reportes.datosReporteResumen.data.porcentaje_total || 0))}
                                   </Text>
                               </View>
                           )}
                       </View>
                   </View>
               </View>
           </Page>
       </Document>
   );
};

// Modificar el componente principal
const Reportes = () => {
    const [evaluacionesNino, setEvaluacionesNino] = useState(null);
    const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
    const [reportes, setReportes] = useState({});
    const [dateFilter, setDateFilter] = useState('');
    const [fechasDisponibles, setFechasDisponibles] = useState([]);
    const [evaluacionesFiltradas, setEvaluacionesFiltradas] = useState(null);
    const [centro, setCentro] = useState(null);

    useEffect(() => {
        cargarEvaluacionesNino();
        cargarDatosCentro();
    }, []);

    useEffect(() => {
        if (evaluacionesNino) {
            // Extraer fechas únicas de las evaluaciones
            const fechasUnicas = [...new Set(
                evaluacionesNino.map(evaluacion => {
                    const fecha = new Date(evaluacion.fecha);
                    // Ajustar la zona horaria para evitar el problema de -1 día
                    fecha.setMinutes(fecha.getMinutes() + fecha.getTimezoneOffset());
                    return formatearFechaLocal(fecha);
                })
            )].sort().reverse();
            setFechasDisponibles(fechasUnicas);
            filtrarEvaluaciones();
        }
    }, [evaluacionesNino, dateFilter]);

    const cargarDatosCentro = async () => {
        try {
            const response = await obtenerCentro();
            if (response.success) {
                setCentro(response.data[0]);
            }
        } catch (error) {
            console.error('Error al cargar datos del centro:', error);
        }
    };

    const cargarEvaluacionesNino = async () => {
        try {
            const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
            if (!hijoSeleccionado) {
                console.error('No hay un hijo seleccionado');
                return;
            }

            const response = await obtenerEvaluaciones();
            if (response.success) {
                const evaluacionesDelNino = response.data.filter(
                    evaluacion => evaluacion.nino.id_child === hijoSeleccionado.id_child
                );
                setEvaluacionesNino(evaluacionesDelNino);
            }
        } catch (error) {
            console.error('Error al cargar evaluaciones:', error);
        }
    };

    const filtrarEvaluaciones = () => {
        if (!evaluacionesNino) return;

        let evaluacionesFiltradas = [...evaluacionesNino];

        if (dateFilter) {
            evaluacionesFiltradas = evaluacionesFiltradas.filter(evaluacion => {
                const fechaObj = new Date(evaluacion.fecha);
                // Ajustar la zona horaria para evitar el problema de -1 día
                fechaObj.setMinutes(fechaObj.getMinutes() + fechaObj.getTimezoneOffset());
                const fechaEval = formatearFechaLocal(fechaObj);
                return fechaEval === dateFilter;
            });
        }

        setEvaluacionesFiltradas(evaluacionesFiltradas);
    };

    const handleEvaluacionClick = async (evaluacionId) => {
        try {
            if (evaluacionSeleccionada === evaluacionId) {
                setEvaluacionSeleccionada(null);
                setReportes({});
            } else {
                setEvaluacionSeleccionada(evaluacionId);
                
                const reporteProgreso = await obtenerReporteProgreso(evaluacionId);
                const reporteResumen = await obtenerReporteResumen(evaluacionId);

                setReportes({
                    [evaluacionId]: {
                        datosReporteProgreso: reporteProgreso,
                        datosReporteResumen: reporteResumen
                    }
                });
            }
        } catch (error) {
            console.error('Error al obtener los reportes:', error);
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const fechaObj = new Date(fecha);
        // Ajustar la zona horaria para evitar el problema de -1 día
        fechaObj.setMinutes(fechaObj.getMinutes() + fechaObj.getTimezoneOffset());
        return fechaObj.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const limpiarFiltros = () => {
        setDateFilter('');
    };

    return (
        <div className="p-6 bg-white rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Reportes de Evaluaciones</h2>

            {/* Filtro de fechas */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Filtros de búsqueda
                </h3>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Filtrar por fecha
                    </label>
                    <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        <option value="">Todas las fechas</option>
                        {fechasDisponibles.map((fecha) => {
                            const fechaObj = new Date(fecha + 'T00:00:00');
                            // Ajustar la zona horaria para evitar el problema de -1 día
                            fechaObj.setMinutes(fechaObj.getMinutes() + fechaObj.getTimezoneOffset());
                            return (
                                <option key={fecha} value={fecha}>
                                    {fechaObj.toLocaleDateString('es-ES', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </option>
                            );
                        })}
                    </select>
                </div>
                {dateFilter && (
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                            Filtro activo: {`Fecha: ${formatearFecha(dateFilter)}`}
                        </span>
                        <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-500">
                                {evaluacionesFiltradas?.length} resultado(s)
                            </span>
                            <button
                                onClick={limpiarFiltros}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                                Limpiar filtros
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {(evaluacionesFiltradas || evaluacionesNino) && (evaluacionesFiltradas || evaluacionesNino).length > 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <h3 className="font-semibold text-gray-700 mb-4">Evaluaciones Disponibles</h3>
                    <div className="space-y-2">
                        {(evaluacionesFiltradas || evaluacionesNino).sort((a, b) => {
                            const fechaA = new Date(a.fecha);
                            const fechaB = new Date(b.fecha);
                            // Ajustar la zona horaria para evitar el problema de -1 día
                            fechaA.setMinutes(fechaA.getMinutes() + fechaA.getTimezoneOffset());
                            fechaB.setMinutes(fechaB.getMinutes() + fechaB.getTimezoneOffset());
                            return fechaB - fechaA;
                        }).map((evaluacion) => (
                            <div key={evaluacion.id_evaluacion}>
                                <div
                                    className={`p-3 rounded-md cursor-pointer ${
                                        evaluacionSeleccionada === evaluacion.id_evaluacion
                                            ? 'bg-blue-50 border border-blue-200'
                                            : 'bg-gray-50 hover:bg-gray-100'
                                    }`}
                                    onClick={() => handleEvaluacionClick(evaluacion.id_evaluacion)}
                                >
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm font-medium text-gray-700">
                                            Fecha: {formatearFecha(evaluacion.fecha)}
                                        </p>
                                        {evaluacionSeleccionada === evaluacion.id_evaluacion && (
                                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                <PDFDownloadLink
                                                    document={
                                                        <ReportePDF
                                                            centro={centro}
                                                            nino={evaluacion.nino}
                                                            evaluacion={evaluacion}
                                                            reportes={reportes[evaluacion.id_evaluacion]}
                                                        />
                                                    }
                                                    fileName={`reporte_${evaluacion.nino.nombre}_${formatearFecha(evaluacion.fecha)}.pdf`}
                                                    className="flex items-center gap-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                                                >
                                                    {({ loading, error }) => (
                                                        <div className="flex items-center gap-2">
                                                            {loading ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                                    <span>Generando PDF...</span>
                                                                </>
                                                            ) : error ? (
                                                                <>
                                                                    <span className="text-red-600">Error al generar PDF</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FileDown className="w-4 h-4" />
                                                                    <span>Descargar PDF</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </PDFDownloadLink>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {evaluacionSeleccionada === evaluacion.id_evaluacion && (
                                    <div className="mt-4">
                                        <EvaluationCharts 
                                            reportes={reportes}
                                            evaluacionId={evaluacionSeleccionada}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-lg font-medium text-gray-900">
                                {dateFilter ? 'No se encontraron resultados' : 'No hay evaluaciones registradas'}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                                {dateFilter ? 'Intente con otros criterios de búsqueda' : 'No se encontraron registros de evaluaciones para generar reportes'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">Por favor, contacte con el personal docente para más información</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reportes;
